import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router";
import { useAppDispatch, useAppSelector } from "../../state/store";
import { SimpleStringInput } from "../Inputs";
import { ReactComponent as SearchIcon } from "../../icons/search.svg";
import { tournamentSearch } from "../../state/actions/tournamentActions";
import { Breadcrumbs, Grid } from "@mui/material";
import { TournamentPage } from "../../types/tournamentTypes";
import { ReactComponent as HomePage } from "../../icons/tournamentIcons/homepage.svg";
import { getPageName } from "../../util/tournamentUtils";

interface HeaderProps {
  page?: TournamentPage;
  pageName: string;
  defaultSearch?: string;
  navigateBack?: string;
}

/**
 * Top header for all tournament pages
 */
export function TournamentHeader(props: HeaderProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.authentication);

  const [isSearching, setIsSearching] = useState(
    props.defaultSearch !== undefined
  );
  const [searchText, setSearchText] = useState(props.defaultSearch || "");
  useEffect(() => {
    document.getElementById("tournament-search-text")?.focus();
  }, [isSearching]);
  const search = () => {
    if (!searchText) return;
    setIsSearching(false);
    dispatch(tournamentSearch(searchText));
    navigate(`/tournaments/search/${encodeURIComponent(searchText)}`);
  };

  return (
    <div style={{ marginBottom: 10 }}>
      <Grid container style={{ marginBottom: 10 }} spacing={2}>
        {!props.page && (
          <Grid>
            <Link
              className="secondary-btn"
              to={props.navigateBack || "/"}
              onClick={() => {
                window.scrollTo(0, 0);
              }}
            >
              Go Back
            </Link>
          </Grid>
        )}
        <Grid>
          <h1 style={{ margin: 0, marginTop: -4 }}>{props.pageName}</h1>
        </Grid>
        <Grid>
          {!isSearching && (
            <button
              className="link"
              onClick={() => setIsSearching(true)}
              aria-label="Search"
            >
              <SearchIcon width={26} height={26} />
            </button>
          )}
          {isSearching && (
            <div>
              <label
                htmlFor="tournament-search-text"
                style={{ position: "absolute", top: -1000 }}
              >
                Search For:
              </label>
              <SimpleStringInput
                value={searchText}
                onChange={setSearchText}
                caption="Search"
                placeholder="Search"
                onKeyUp={(e) => {
                  if (!e.code) return;
                  if (e.code.includes("Enter")) {
                    search();
                    document.getElementById("tournament-search-text")?.blur();
                  }
                  if (e.code === "Escape") setIsSearching(false);
                  window.scrollTo(0, 0);
                }}
                id="tournament-search-text"
              />
              <button className="link" onClick={search}>
                <SearchIcon width={26} height={26} aria-label="Search" />
              </button>
            </div>
          )}
        </Grid>
        <Grid>
          {user?.securityClearance.logonType === "ADMIN" && (
            <>
              {props.page?.pageId && (
                <Link
                  style={{ marginRight: 20, marginTop: 8 }}
                  to={`/tournaments/edit/${props.page.pageId}/${
                    props.page.secondaryId || ""
                  }`.replace(/ /gi, "_")}
                  onClick={() => window.scrollTo(0, 0)}
                >
                  Edit
                </Link>
              )}
              <Link
                style={{ marginRight: 20, marginTop: 8 }}
                to="/tournaments/admin"
                onClick={() => window.scrollTo(0, 0)}
              >
                Admin
              </Link>
              {props.page?.pageId !== "Help" && (
                <Link
                  style={{ marginTop: 8, marginRight: 20 }}
                  to="/tournaments/help"
                  onClick={() => window.scrollTo(0, 0)}
                >
                  Help
                </Link>
              )}
              <Link
                style={{ marginTop: 8 }}
                to="/tournaments/create"
                onClick={() => window.scrollTo(0, 0)}
              >
                Create
              </Link>
            </>
          )}
        </Grid>
      </Grid>
      {props.page && (
        <Breadcrumbs
          separator="/"
          sx={{
            li: { listStyle: "none", marginLeft: 0.5, marginRight: 0.5 },
          }}
        >
          <Link to="/" className="link" aria-label="BQA Home">
            <HomePage width={14} height={14} />
          </Link>
          <Link to="/tournaments" className="link">
            Tournaments
          </Link>
          {props.page.secondaryId ? (
            <Link
              to={`/tournaments/${props.page.pageId}`.replace(/ /g, "_")}
              className="link"
            >
              {props.page.pageId}
            </Link>
          ) : (
            <span>{getPageName(props.page)}</span>
          )}
          {props.page.secondaryId && <span>{props.page.secondaryId}</span>}
        </Breadcrumbs>
      )}
    </div>
  );
}
